import { isRecord } from "./typeGuards"

/**
 * RFC 9457 problem detail payload used for backend error responses.
 */
export interface ProblemDetail extends Record<string, unknown> {
  type?: string
  title?: string
  status?: number
  detail?: string | null
  instance?: string | null
  code?: string | null
  resource?: string | null
  context?: Record<string, unknown> | null
}

const toMessage = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

/**
 * Extracts a human-readable message from a problem detail or legacy error payload.
 *
 * @param payload - Error payload returned from the backend.
 * @param status - HTTP status code associated with the error.
 * @returns A meaningful message describing the error.
 */
export const extractErrorMessage = (payload: unknown, status?: number): string => {
  const messageFromString = toMessage(payload)
  if (messageFromString) {
    return messageFromString
  }

  if (isRecord(payload)) {
    const candidateMessages = [
      payload.detail,
      payload.title,
      payload.message,
      payload.error,
      payload.code,
      payload.resource,
    ]

    for (const candidate of candidateMessages) {
      const message = toMessage(candidate)
      if (message) {
        return message
      }
    }
  }

  if (status) {
    return `Request failed with status ${status}`
  }

  return "Network request failed"
}

/**
 * Extracts a human-readable error message with a custom fallback.
 *
 * @param error - Unknown error payload to extract message from.
 * @param fallback - Message to use if no meaningful message can be extracted.
 * @returns A user-friendly error message.
 */
export const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }

  if (isRecord(error)) {
    const extracted = extractErrorMessage(error)
    // If extractErrorMessage returns a generic message, use the fallback instead
    if (extracted !== "Network request failed") {
      return extracted
    }
  }

  return fallback
}
